import * as React from "react";
import { styled } from "@mui/material/styles";
import { Box, Divider, Fade, Typography } from "@mui/material";
import {
  addressLookup,
  createUrlSafePathing,
  fileNameFormat,
  toTitleCase,
} from "../../common/services/Utilities";
import {
  KeyStringValue,
  KeyValue,
  OscrLevelTwoRecord,
  Query,
  SearchPillData,
  SimpleTableRow,
  SortDirection,
  SortProperties,
  TableHeader,
} from "../../interfaces";
import {
  BRANCH_OFFICE_RELOCATIONS,
  branchOfficeRelocation,
  BUSINESS_COMBINATIONS,
  BUSINESS_COMBINATIONS_FAILURES,
  businessCombinationFailures,
  businessCombinations,
  CHANGECODE_DESC,
  CONVERSIONS,
  conversions,
  FORMATTED_DATE,
  FROM_ADDRESS,
  FROM_OFFICE_ADDRESS,
  INTERIM_MERGERS,
  interimMergers,
  levelTwoOrderedKeysLookup,
  LIQUIDATIONS,
  liquidations,
  MAIN_OFFICE_RELOCATIONS,
  mainOfficeRelocations,
  NEW_INSTITUTIONS,
  newInstitutions,
  OFF_SERVTYPE_DESC,
  OFFICE_ADDRESS,
  OFFICE_CLOSINGS,
  OFFICE_OPENINGS,
  officeClosingHeaders,
  officeOpeningsHeaders,
  PANDAS,
  PHYSICAL_ADDRESS,
  purchaseAssumptionBranches,
  structureChangeLevelTwoPathingMap,
  TITLE_CHANGES,
  titleChanges,
  TRUST_POWERS,
  trustPowers,
  VIEW_DETAILS,
  SUR_CHANGECODE_DESC,
  defaultStructureChangeLevelTwoSortFields,
} from "../../refdata/constants";
import SearchPills from "../../containers/SearchPills";
import ResultPagination from "../../containers/ResultPagination";
import MultiHeaderTable from "../MultiHeaderTable/MultiHeaderTable";
import ModalDialog from "../ModalDialog";
import { AppUrl } from "../../config";
import AddressDisplay from "../AddressDisplay";
import InfoIcon from "@mui/icons-material/Info";
import SimpleTableBody from "../MultiHeaderTable/SimpleTableBody";
import { useParams, useNavigate } from "react-router-dom";
import ViewInstitutionModalSubtitle from "../ViewInstitutionModalSubtitle";
import StructureChangeModalDialog from "./StructureChangeModalDialog";
import ApiDataDownload from "../ApiDataDownload";
import { getHistoryAggregationApiCalls } from "../../common/services/HistoryUtilities";
import { fetchData } from "../../common/services/ApiClient";
import { eventCode } from "../../refdata/FilterOptions";
import { useInitialPageLoad } from "../../hooks/useInitialPageLoad";
import ShowHideButton from "../ShowHideButton";
import ResultsViewToggle from "../FusionCharts/ResultsViewToggle";
import TableChartIcon from "@mui/icons-material/TableChart";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import TimelineIcon from "@mui/icons-material/Timeline";
import USAMap from "../FusionCharts/BSCUSAMAP";
import BSCFinancialsChart from "../FusionCharts/BSCChart";

const PREFIX = "StructureChangeLevelTwo";

const classes = {
  branchesButton: `${PREFIX}-branchesButton`,
  PANDAFootnote: `${PREFIX}-PANDAFootnote`,
};

const Root = styled("div")(({ theme }) => ({
  [`& .${classes.branchesButton}`]: {
    "height": 40,
    "width": 160,

    "&.MuiButton-root": {
      margin: "10px auto",
      textAlign: "center",
      display: "block",
    },
  },

  [`& .${classes.PANDAFootnote}`]: {
    color: "rgb(102, 102, 102)",
    maxWidth: 432,
    marginLeft: 5,
    paddingLeft: 8,
    paddingBottom: 8,
    fontSize: 13,
    lineHeight: "15px",
    marginTop: -24,
  },
}));

interface Props {
  appState: Query;
  oscrSearchPillData: SearchPillData[];
  sortProperties: SortProperties;
  onResultSuccess: () => void;
  onResultFailure: (error: Error) => void;
  updateSort: (sortField: string, sortDirection: SortDirection) => void;
}

export function OSCRAddressDownload(
  data: OscrLevelTwoRecord,
  addressType: string,
) {
  const addressObject = addressLookup(data)[addressType];

  return addressObject.address &&
    addressObject.city &&
    addressObject.state &&
    addressObject.zip
    ? `${addressObject.address}, ${addressObject.city}, ${addressObject.state} ${addressObject.zip}`
    : "";
}

export function generateViewInstitutionsHref(
  oscrRecord: OscrLevelTwoRecord,
  eventType: string,
) {
  const cert: { [key: string]: string } = {
    [BUSINESS_COMBINATIONS_FAILURES]: "SUR_CERT",
    [BUSINESS_COMBINATIONS]: "SUR_CERT",
    [PANDAS]: "ACQ_CERT",
  };
  return `${AppUrl("BankFind")}/details/${
    cert[eventType] ? oscrRecord[cert[eventType]] : oscrRecord.CERT
  }`;
}

export function formattedValues(
  key: string,
  levelTwoRecord: OscrLevelTwoRecord,
  download?: boolean,
  eventType?: string,
) {
  switch (key) {
    case PHYSICAL_ADDRESS:
    case OFFICE_ADDRESS:
    case FROM_ADDRESS:
    case FROM_OFFICE_ADDRESS:
      return download ? (
        OSCRAddressDownload(levelTwoRecord, key)
      ) : (
        <AddressDisplay
          bold={
            (eventType === "Main Office Relocations" &&
              key === "PHYSICAL_ADDRESS") ||
            ((eventType === "Branch Office Openings" ||
              eventType === "Branch Office Closings" ||
              eventType === "Branch Office Relocations") &&
              key === "OFFICE_ADDRESS")
          }
          addressObject={addressLookup(levelTwoRecord)[key]}
          useFullStateName={true}
        />
      );
    case FORMATTED_DATE:
      return new Date(levelTwoRecord.EFFDATE).toLocaleDateString("en-US", {
        month: "2-digit",
        day: "2-digit",
        year: "numeric",
        timeZone: "UTC",
      });
    case OFF_SERVTYPE_DESC:
      return toTitleCase(levelTwoRecord.OFF_SERVTYPE_DESC);
    case CHANGECODE_DESC:
      return `${toTitleCase(levelTwoRecord.CHANGECODE_DESC)}${
        levelTwoRecord.CHANGECODE === 224 &&
        levelTwoRecord.CHANGECODE_DESC.slice(-1) !== ")"
          ? ")"
          : ""
      }`;
    case SUR_CHANGECODE_DESC:
      return toTitleCase(levelTwoRecord.SUR_CHANGECODE_DESC);
    case VIEW_DETAILS:
      return <InfoIcon color="primary" />;
    default:
      return levelTwoRecord[key] as string | number | React.ReactElement;
  }
}

export function getLevelThreeTitle(
  eventType: string,
  oscrLevelThreeDetails: OscrLevelTwoRecord,
) {
  const titleLookup: KeyValue = {
    [BUSINESS_COMBINATIONS]: oscrLevelThreeDetails.SUR_INSTNAME,
    [BUSINESS_COMBINATIONS_FAILURES]: oscrLevelThreeDetails.SUR_INSTNAME,
    [PANDAS]: oscrLevelThreeDetails.ACQ_INSTNAME,
  };

  return titleLookup[eventType]
    ? titleLookup[eventType]
    : oscrLevelThreeDetails.INSTNAME;
}

export const modalSubtitle = (
  oscrRecord: OscrLevelTwoRecord,
  eventType: string,
  classes: KeyStringValue,
  oscrLevelFour: KeyValue,
  onclick: () => void,
) => {
  const showViewInstitution =
    oscrRecord.CLASS && !["NC", "NS", "CU"].includes(oscrRecord.CLASS);

  const branchOfficeCount = oscrRecord.ACQ_BRANCHES
    ? `${oscrRecord.ACQ_BRANCHES.toLocaleString()} ${
        oscrRecord.ACQ_BRANCHES === 1 ? "Branch Office" : "Branch Offices"
      }`
    : "";

  return (
    <Box
      sx={
        showViewInstitution
          ? {
              paddingBottom: 1,
            }
          : {}
      }
    >
      {eventType}{" "}
      <Fade
        in={oscrLevelFour.resultData && oscrLevelFour.resultData.length > 0}
      >
        <Typography color="primary" style={{ display: "inline-block" }}>
          - {branchOfficeCount}
        </Typography>
      </Fade>
      {showViewInstitution ? (
        <ViewInstitutionModalSubtitle onClick={onclick} />
      ) : undefined}
    </Box>
  );
};

export function showViewBranchesButton(eventType: string) {
  const viewableEventTypes = [
    "Business Combinations",
    "Business Combinations - Failures",
    "Branch Purchases & Assumptions by Institution",
    "Liquidations",
  ];
  return viewableEventTypes.includes(eventType);
}

export function tableHeaderValues(eventType: string) {
  const header: { [key: string]: TableHeader[][] } = {
    [NEW_INSTITUTIONS]: newInstitutions,
    [BUSINESS_COMBINATIONS]: businessCombinations,
    [BUSINESS_COMBINATIONS_FAILURES]: businessCombinationFailures,
    [INTERIM_MERGERS]: interimMergers,
    [CONVERSIONS]: conversions,
    [TITLE_CHANGES]: titleChanges,
    [MAIN_OFFICE_RELOCATIONS]: mainOfficeRelocations,
    [LIQUIDATIONS]: liquidations,
    [TRUST_POWERS]: trustPowers,
    [BRANCH_OFFICE_RELOCATIONS]: branchOfficeRelocation,
    [PANDAS]: purchaseAssumptionBranches,
    [OFFICE_OPENINGS]: officeOpeningsHeaders,
    [OFFICE_CLOSINGS]: officeClosingHeaders,
  };

  return header[eventType];
}

export function transformLevelTwoData(
  levelTwoResponse: OscrLevelTwoRecord[],
  eventType: string,
  setLevelThree: (val: OscrLevelTwoRecord) => void,
  setDisplay: (val: boolean) => void,
  download?: boolean,
): SimpleTableRow[] {
  const linkStyle = {
    color: "blue",
    fontWeight: "bold",
    textDecoration: "underline",
  };
  return levelTwoResponse.map((levelTwoRecord: OscrLevelTwoRecord) => {
    const tableCellData: SimpleTableRow = {};
    levelTwoOrderedKeysLookup[eventType].forEach((key: string) => {
      let styling = {};
      if (
        key === "INSTNAME" ||
        key === "SUR_INSTNAME" ||
        (key === "ACQ_INSTNAME" &&
          eventType === "Branch Purchases & Assumptions by Institution")
      ) {
        if (eventType.startsWith("Branch Office")) {
          styling = {};
        } else if (eventType !== "Main Office Relocations") {
          styling = linkStyle;
        }
      }
      if (
        eventType === "Main Office Relocations" &&
        key === "PHYSICAL_ADDRESS"
      ) {
        styling = linkStyle;
      }
      if (
        (eventType === "Branch Office Openings" ||
          eventType === "Branch Office Closings" ||
          eventType === "Branch Office Relocations") &&
        key === "OFFICE_ADDRESS"
      ) {
        styling = linkStyle;
      }
      tableCellData[key] = {
        value: download
          ? formattedValues(key, levelTwoRecord, true, eventType)
          : formattedValues(key, levelTwoRecord, undefined, eventType),
        styles: styling,
      };
    });

    tableCellData.rowOnClick = (e: any) => {
      if (e.key === "Enter" || e.nativeEvent.type === "click") {
        setLevelThree(levelTwoRecord);
        setDisplay(true);
      }
    };
    return tableCellData;
  });
}

const expandHeaderRow = (row: TableHeader[]): string[] => {
  return row.flatMap((cell) => {
    const span = cell.colSpan ?? 1;
    const title = typeof cell.title === "string" ? cell.title : "";
    return Array(span).fill(title);
  });
};

const generateDownloadHeaders = (eventType: string): string[] => {
  const headerRows = tableHeaderValues(eventType);
  const orderedKeys = levelTwoOrderedKeysLookup[eventType];

  const expandedRows = headerRows.map((row) => expandHeaderRow(row));
  const columnCount = expandedRows[expandedRows.length - 1].length;

  const combinedHeaders = Array.from({ length: columnCount }, (_, col) => {
    const levelTitles = expandedRows
      .map((row) => row[col])
      .filter((title) => title && title.trim() !== "");

    return levelTitles.join(" ");
  });

  return combinedHeaders.filter(
    (_header, index) => orderedKeys[index] !== VIEW_DETAILS,
  );
};

const generateDownloadRows = (
  levelTwoResponse: OscrLevelTwoRecord[],
  eventType: string,
): string[][] => {
  const orderedKeys = levelTwoOrderedKeysLookup[eventType].filter(
    (key: string) => key !== VIEW_DETAILS,
  );

  return levelTwoResponse.map((record: OscrLevelTwoRecord) =>
    orderedKeys.map((key: string) => {
      const value = formattedValues(key, record, true, eventType);
      return value === undefined || value === null ? "" : String(value);
    }),
  );
};

const structureChangeDownloadData = (
  levelTwoResponse: OscrLevelTwoRecord[],
  eventType: string,
): string[][] => {
  return [
    generateDownloadHeaders(eventType),
    ...generateDownloadRows(levelTwoResponse, eventType),
  ];
};

export default function StructureChangeLevelTwo({
  appState,
  oscrSearchPillData,
  sortProperties,
  updateSort,
}: Props) {
  const chartInstanceRef = React.useRef<any>(null);

  const [oscrLevelFour, setOscrLevelFour] = React.useState({
    resultData: [],
    dataLoaded: false,
    timeout: 0,
  });
  const [displayDialog, setDisplayDialog] = React.useState(false);
  const [oscrLevelThreeDetails, setOscrLevelThreeDetails] =
    React.useState<OscrLevelTwoRecord>({
      TRANSNUM: 0,
      CHANGECODE: 0,
      REPORT_TYPE: 0,
      CHANGECODE_DESC: "",
      SUR_CHANGECODE_DESC: "",
      PROCDATE: "",
      EFFDATE: "",
      ENDDATE: "",
      UNINUM: 0,
      ORG_ROLE_CDE: 0,
      INSTNAME: "",
      SUR_INSTNAME: "",
      OFF_SERVTYPE_DESC: "",
    });
  const [levelTwoState, setLevelTwoState] = React.useState({
    createTimestamp: "",
    recordCount: 0,
    resultData: [],
  });
  const [dataView, setDataView] = React.useState<"table" | "map" | "chart">(
    "table",
  );
  const [mapData, setMapData] = React.useState<
    {
      id: string;
      value: number;
    }[]
  >([]);

  const [classData, setClassData] = React.useState<KeyValue>({
    classes: [],
    data: [{}],
  });
  let { eventType } = useParams();
  eventType = structureChangeLevelTwoPathingMap[eventType.split("?")[0]];
  const navigate = useNavigate();

  const showHideSearchPills = (
    <>
      <Divider />
      <Root>
        <ShowHideButton
          title="Selected Search Criteria"
          body={<SearchPills dataArray={oscrSearchPillData} />}
        />
      </Root>
    </>
  );

  const subTitle = modalSubtitle(
    oscrLevelThreeDetails,
    eventType,
    classes,
    oscrLevelFour,
    () =>
      navigate(
        {
          pathname: generateViewInstitutionsHref(
            oscrLevelThreeDetails,
            eventType,
          ),

          search: "?bankfindLevelThreeView=History",
        },
        {
          state: {
            url: `${AppUrl("OSCR")}/${createUrlSafePathing(eventType)}${
              window.location.search
            }`,
            locationCity: appState.search.city,
            locationState: appState.search.stateSearch,
            fromOSCR: true,
          },
        },
      ),
  );
  const transformedData = transformLevelTwoData(
    levelTwoState.resultData,
    eventType,
    setOscrLevelThreeDetails,
    setDisplayDialog,
  );

  React.useEffect(() => {
    const arrayOfTitles = eventCode.map((event) => event.label);
    const currentEventType =
      structureChangeLevelTwoPathingMap[
        window.location.pathname.split("/").slice(-1)[0]
      ];
    // The second check is due to the delay in change from the route from using
    // the browser back button so we want to ensure that the path is correct before making the api calls as it gets rerendered still during the change in paths
    if (eventType && arrayOfTitles.includes(currentEventType)) {
      let isMounted = true;
      const historyApiArray = getHistoryAggregationApiCalls(
        appState,
        false,
        eventType,
      );
      const stateAgg = eventType.includes("Branch Office")
        ? "OFF_PSTALP"
        : "PSTALP";
      const aggApiState = getHistoryAggregationApiCalls(
        appState,
        false,
        eventType,
        stateAgg,
      );
      const aggApiClass = getHistoryAggregationApiCalls(
        appState,
        false,
        eventType,
        appState.filters.searchDateRadio === "EFFDATE" ? "EFFYEAR" : "PROCYEAR",
        ["CLASS"],
      );
      (async () => {
        await Promise.all(
          historyApiArray.map((path: string) => fetchData(path)),
        )
          .then((response) => {
            if (isMounted) {
              const responseData = [].concat(
                ...response.map((res) => res.data as never[]),
              );
              const recordCount = response.reduce(
                (count, data) => count + data.totals.count,
                0,
              );
              setLevelTwoState({
                resultData: responseData,
                recordCount,
                createTimestamp: response[0].createTimestamp,
              });
            }
          })
          .catch((error: Error) => {
            console.error(error);
          });
      })();
      (async () => {
        await Promise.all(aggApiState.map((path: string) => fetchData(path)))
          .then((response) => {
            if (isMounted) {
              const responseData = []
                .concat(...response.map((res) => res.data as never[]))
                .map((obj: any) => {
                  const newObj = { id: obj[stateAgg], value: obj.count };
                  return newObj;
                });
              setMapData(responseData);
            }
          })
          .catch((error: Error) => {
            console.error(error);
          });
      })();
      (async () => {
        await Promise.all(aggApiClass.map((path: string) => fetchData(path)))
          .then((response) => {
            if (isMounted) {
              const responseKeys = Object.keys(response[0].totals);
              const classList = responseKeys
                .map((key) => {
                  if (key !== "count") {
                    return key.replace("count_CLASS_", "");
                  } else {
                    return "";
                  }
                })
                .filter((key) => key);
              const cleanResponse = response[0].data.map((res: KeyValue) => {
                return Object.fromEntries(
                  Object.entries(res).map(([key, value]) => [
                    key.replace("count_CLASS_", ""), // Removes "prefix_" from every key
                    value,
                  ]),
                );
              });

              setClassData({ classes: classList, data: cleanResponse });
            }
          })
          .catch((error: Error) => {
            console.error(error);
          });
      })();
      return () => {
        isMounted = false;
      };
    }
  }, [appState, eventType]);

  React.useEffect(() => {
    if (!appState.sort.sortField) {
      updateSort(defaultStructureChangeLevelTwoSortFields[eventType], "ASC");
    }
  }, [appState.sort.sortField, eventType, updateSort]);

  useInitialPageLoad("oscrDetails");

  const bankClassCategories = classData.classes.map((val: any) => ({
    seriesName: val,
    data: classData.data.map((data: any) => {
      return {
        value: data[val],
        tooltext: `${val}: ${data[val] || 0}`,
      };
    }),
  }));

  const dataViz =
    dataView === "map" ? (
      <USAMap
        mapData={mapData}
        usaTotal={levelTwoState.recordCount}
        eventType={eventType}
      />
    ) : dataView === "chart" ? (
      <Box
        id="locations-chart"
        sx={{
          "& [class*='toolbar-master']": { display: "none" }, // hides the hamburger menu for image download
        }}
      >
        <BSCFinancialsChart
          categories={[
            {
              category: classData.data.map((data: KeyValue) => {
                return {
                  label:
                    data[
                      appState.filters.searchDateRadio === "EFFDATE"
                        ? "EFFYEAR"
                        : "PROCYEAR"
                    ],
                };
              }),
            },
          ]}
          dataset={bankClassCategories}
          onRender={(chart: any) => {
            chartInstanceRef.current = chart;
          }}
        />
      </Box>
    ) : (
      <ResultPagination count={levelTwoState.recordCount}>
        <MultiHeaderTable
          sortProperties={sortProperties}
          tableHeaders={tableHeaderValues(eventType)}
          tableBody={<SimpleTableBody tableData={transformedData} />}
        />
      </ResultPagination>
    );

  return (
    <Root>
      <Typography component="div">
        <>
          <Box
            id="multi-header-table-title"
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography variant="h2" id="resultSummary">
              {" "}
              {`${eventType} - ${levelTwoState.recordCount.toLocaleString()} ${
                levelTwoState.recordCount === 1
                  ? "Matching Result"
                  : "Matching Results"
              }`}
            </Typography>

            <ApiDataDownload
              apiPathLink="history"
              app="structureChange"
              appState={appState}
              numberOfResults={levelTwoState.recordCount}
              apiData={[
                {
                  appState,
                  fileName: `${fileNameFormat(eventType)}_formatted.csv`,
                  apiPath: "history",
                  text: `Download ${eventType} Results Data - Formatted`,
                  transformData: (rows: KeyValue[]) =>
                    structureChangeDownloadData(
                      rows as OscrLevelTwoRecord[],
                      eventType,
                    ),
                },
              ]}
            />
          </Box>
          {showHideSearchPills}
        </>
      </Typography>
      <ResultsViewToggle
        mode={dataView}
        onModeChange={(mode: string) =>
          setDataView(mode as "table" | "map" | "chart")
        }
        options={[
          { label: "table", icon: <TableChartIcon /> },
          { label: "map", icon: <PlaceOutlinedIcon /> },
          { label: "chart", icon: <TimelineIcon /> },
        ]}
      />
      {dataViz}

      <ModalDialog
        title={getLevelThreeTitle(eventType, oscrLevelThreeDetails)}
        subtitle={subTitle}
        display={displayDialog}
        setDisplay={setDisplayDialog}
        id="oscr"
        onExited={() => {
          setOscrLevelFour({
            resultData: [],
            dataLoaded: false,
            timeout: 0,
          });
        }}
      >
        <StructureChangeModalDialog
          dataLoaded={oscrLevelFour.dataLoaded}
          timeout={oscrLevelFour.timeout}
          showViewBranches={showViewBranchesButton(eventType)}
          eventType={eventType}
          data={oscrLevelThreeDetails}
          viewBranchesData={oscrLevelFour.resultData}
          setViewBranchesData={setOscrLevelFour}
          appState={appState}
        />
        {eventType === PANDAS && !oscrLevelThreeDetails.OUT_CERT ? (
          <Typography className={classes.PANDAFootnote} id={"PANDA_footnote"}>
            *Please use the “Contact the FDIC” button for additional details
            pertaining to this event.
          </Typography>
        ) : (
          ""
        )}
      </ModalDialog>
    </Root>
  );
}
